import type { ReactNode } from 'react';
import { Star } from 'lucide-react';
import { Avatar, Card, cn } from '@tcrd/ui';
import { VehicleIllustration } from './VehicleIllustration';

export interface HeroDriver {
  firstName: string;
  lastName: string;
  photoUrl: string | null;
  rating: string;
  trips: number;
  presence?: string;
  vehicle: { make: string; model: string; year: number; color: string; type: string; plate: string; photoUrl: string | null } | null;
}

const PRESENCE: Record<string, { label: string; cls: string; dot: string }> = {
  AVAILABLE: { label: 'En línea', cls: 'bg-green-600 text-white', dot: 'bg-green-500' },
  BUSY: { label: 'En un viaje', cls: 'bg-amber-500 text-white', dot: 'bg-amber-400' },
  OFFLINE: { label: 'Desconectado', cls: 'bg-[var(--surface-2)] text-[var(--text-muted)]', dot: 'bg-gray-400' },
};

/**
 * Tarjeta del conductor (como en las apps grandes): foto con estado, nombre, puntuación y viajes,
 * vehículo con su foto (o ilustración) y la placa. `status` permite poner un control en lugar del estado.
 */
export function DriverHeroCard({ d, status, label, className }: { d: HeroDriver; status?: ReactNode; label?: string; className?: string }) {
  const p = PRESENCE[d.presence ?? 'OFFLINE'] ?? PRESENCE.OFFLINE!;
  const rating = Number(d.rating);
  return (
    <Card className={cn('p-4 flex items-center gap-3', className)}>
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        {label && <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">{label}</p>}
        <div className="flex items-center gap-3">
          <span className="relative shrink-0">
            <Avatar src={d.photoUrl} firstName={d.firstName} lastName={d.lastName} size={56} />
            {d.presence && <span className={cn('absolute bottom-0.5 right-0.5 size-3.5 rounded-full border-2 border-[var(--surface)]', p.dot)} aria-hidden />}
          </span>
          <div className="min-w-0">
            <p className="text-base font-bold leading-tight line-clamp-2" data-person>{d.firstName} {d.lastName}</p>
            <p className="text-sm flex items-center gap-1 whitespace-nowrap">
              <Star className="size-4 fill-current text-amber-500" aria-hidden />
              <strong>{rating > 0 ? rating.toFixed(1) : 'Nuevo'}</strong>
              <span className="text-[var(--text-muted)]">({d.trips} viaje{d.trips === 1 ? '' : 's'})</span>
            </p>
            {d.vehicle && <p className="text-sm text-[var(--text-muted)] truncate">{d.vehicle.make} {d.vehicle.model} {d.vehicle.year}</p>}
          </div>
        </div>
        {status ?? (d.presence && <span className={cn('self-start rounded-full px-3 py-1 text-xs font-semibold', p.cls)}>{p.label}</span>)}
      </div>
      {d.vehicle && (
        <div className="shrink-0 flex flex-col items-center gap-1 w-[6.5rem]">
          {d.vehicle.photoUrl ? (
            <img src={d.vehicle.photoUrl} alt={`${d.vehicle.make} ${d.vehicle.model}`} className="h-14 w-[6.5rem] rounded-xl object-cover" />
          ) : (
            <VehicleIllustration type={d.vehicle.type} color={d.vehicle.color} className="w-[6.5rem] h-auto" title={`${d.vehicle.make} ${d.vehicle.model}`} />
          )}
          <span className="rounded-lg border border-[var(--border)] bg-[var(--surface)] px-2 py-0.5 font-mono text-sm font-bold tracking-wide">{d.vehicle.plate}</span>
        </div>
      )}
    </Card>
  );
}
